import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { whereaboutsApi } from '../api/whereabouts';
import { useServerData } from '../hooks/useServerData';
import { describeFreshness } from '../utils/freshness';
import './Whereabouts.css';

// City -> [bg, fg]; spec palette + fallback for unseen cities.
const CITY_COLORS = {
  London: ['#123832', '#5eead4'], Zurich: ['#3a2320', '#fca5a5'],
  'Santa Barbara': ['#2a2340', '#c4b5fd'], Oregon: ['#183028', '#86efac'],
  SF: ['#2a2340', '#c4b5fd'], 'New York': ['#12283c', '#7dd3fc'],
  Chicago: ['#33260f', '#fdba74'], Tokyo: ['#33290f', '#fcd34d'],
  OOO: ['#222630', '#9ca3af'], LA: ['#2a2340', '#c4b5fd'],
};
const FALLBACK = [['#1a2c3c', '#93c5fd'], ['#33220f', '#fdba74'], ['#2b1633', '#f0abfc'], ['#123028', '#6ee7b7']];
const BADGE = {
  workingLocation: ['Working location', '#123832', '#5eead4'],
  all_day_title: ['All-day event', '#12283c', '#7dd3fc'],
  travel: ['Travel', '#33290f', '#fcd34d'],
  home_base: ['Home city · default', '#222630', '#9ca3af'],
  ooo: ['Out of office', '#222630', '#9ca3af'],
};
const SET_LOC_URL = 'https://calendar.google.com/calendar/u/0/r/settings/general';

function makeColorFn() {
  let fi = 0;
  const cache = { ...CITY_COLORS };
  return (c) => {
    if (!cache[c]) { cache[c] = FALLBACK[fi % FALLBACK.length]; fi += 1; }
    return cache[c];
  };
}

// Out-of-office is a FLAG, not a place. The backend now sends the real city
// plus `ooo: true` — an event titled "OOO - Shanti in Oregon" states BOTH facts,
// and a board whose job is "where is everyone" must not answer "nowhere".
function cellLabel(st) {
  return `${st.loc}${st.travel ? ' ✈' : ''}${st.ooo ? ' 🌴' : ''}`;
}

function dlabel(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
}

// Sat/Sun. Shanti asked for the ENTIRE month (2026-07-08) — a weekday-only grid
// silently dropped Aug 1-2. Weekends are shown, but recessed: a weekend row is
// context, not signal.
function isWeekend(iso) {
  const wd = new Date(iso + 'T00:00:00').getDay();
  return wd === 0 || wd === 6;
}

// The backend renames `weekdays` -> `days` now that weekends are included, and
// serves both during the transition. Read the new key, fall back to the old, so
// this page works against either revision.
function monthDays(m) {
  return m.days || m.weekdays || [];
}

export default function Whereabouts() {
  const [mi, setMi] = useState(0);
  const [pinnedMonth, setPinnedMonth] = useState(false); // don't yank the user's month on revalidate
  // The month the user is LOOKING AT, by identity (label), not by index. The
  // backend serves a rolling window, so when revalidation advances it, index 1
  // silently stops meaning "August". Identity survives the shift.
  const monthKeyRef = useRef(null);
  const [hover, setHover] = useState(null); // { content, x, y }
  const showT = useRef(null);
  const hideT = useRef(null);
  const color = useMemo(() => makeColorFn(), []);

  // Server state lives in the L2 hook, not in this page. It fetches on mount
  // AND revalidates when the tab regains focus — the board refreshes nightly,
  // so a tab left open used to render whatever it fetched at load time forever.
  // `validate` makes a malformed HTTP-200 during a BACKGROUND refresh behave
  // like a failed refresh (keep the good board, show the banner) instead of
  // overwriting good data; the render guard below stays as the last resort for
  // the INITIAL load.
  const fetchGrid = useCallback(() => whereaboutsApi.getMonthGrid(12), []);
  const { data, error, loading, isValidating, fetchedAt, refresh } = useServerData(fetchGrid, {
    validate: (d) => Array.isArray(d?.months) && d.months.length > 0,
  });
  const err = error ? (error.message || 'Failed to load whereabouts.') : null;

  const goTo = useCallback((idx) => {
    setMi(idx);
    monthKeyRef.current = data?.months?.[idx]?.label ?? null; // pin by identity
  }, [data]);

  // Land on the current month once; never re-jump the user mid-browse. After
  // that, every new payload re-resolves the pinned month BY IDENTITY — if the
  // rolling window shifted, follow the month, not the index; if the month fell
  // out of the window, clamp.
  useEffect(() => {
    if (!data || !Array.isArray(data.months) || data.months.length === 0) return;
    if (!pinnedMonth) {
      const idx = data.months.findIndex((m) => monthDays(m).includes(data.today));
      const landed = idx >= 0 ? idx : 0;
      setMi(landed);
      monthKeyRef.current = data.months[landed]?.label ?? null;
      setPinnedMonth(true);
      return;
    }
    const key = monthKeyRef.current;
    const idx = key ? data.months.findIndex((m) => m.label === key) : -1;
    if (idx >= 0) setMi(idx);
    else setMi((i) => Math.min(i, data.months.length - 1));
  }, [data, pinnedMonth]);

  const peek = (content, e) => {
    clearTimeout(hideT.current);
    const x = Math.min(e.clientX + 14, window.innerWidth - 306);
    const y = Math.min(e.clientY + 14, window.innerHeight - 260);
    if (hover) { setHover({ content, x, y }); return; }
    clearTimeout(showT.current);
    showT.current = setTimeout(() => setHover({ content, x, y }), 150);
  };
  const drop = () => {
    clearTimeout(showT.current);
    clearTimeout(hideT.current);
    hideT.current = setTimeout(() => setHover(null), 300);
  };
  const holdCard = () => { clearTimeout(hideT.current); clearTimeout(showT.current); };

  if (loading) return <div className="wb-wrap"><div className="wb-loading">Loading team whereabouts…</div></div>;
  // Error blanks the page ONLY when there is no data to show — a failed
  // BACKGROUND revalidation keeps the last good board on screen (the hook's
  // whole contract); the stale-data banner below carries the failure.
  if (err && !data) return <div className="wb-wrap"><div className="wb-error">Couldn’t load whereabouts: {err}</div></div>;
  // Malformed payload guard (the old pre-hook fetch had this): a response
  // without months must render the error view, not TypeError into a blank page.
  if (!data || !Array.isArray(data.months) || data.months.length === 0) {
    return <div className="wb-wrap"><div className="wb-error">Couldn’t load whereabouts: malformed response (no months)</div></div>;
  }

  // Freshness is PROJECTED from the backend's canonical contract, not invented
  // here. `fetchedAt` (from the hook) adds the client-side time elapsed since
  // the payload arrived — without it a long-lived tab reports the fetch-time
  // age forever and never crosses the stale threshold.
  const freshness = describeFreshness(data, new Date(), fetchedAt);

  const M = data.months[Math.min(mi, data.months.length - 1)];
  const statsFor = (name) => M.stats[name] || { home: '', cities: [], explicit_days: 0, home_days: 0, travel_days: 0 };
  const seenCities = [...new Set(data.team.flatMap((p) => statsFor(p.name).cities))];

  const cellCard = (name, iso, st) => {
    const [bt, bbg, bfg] = st.ooo ? BADGE.ooo : (BADGE[st.src] || BADGE.home_base);
    return (
      <>
        <h4>{name} · {dlabel(iso)}</h4>
        <div className="wb-hc-loc">{cellLabel(st)}</div>
        <span className="wb-badge" style={{ background: bbg, color: bfg }}>{bt}</span>
        <div className="wb-sec"><div className="wb-k">How we know this</div>{st.explain}</div>
        {st.evidence && <div className="wb-sec"><div className="wb-k">Evidence</div><span className="wb-ev">“{st.evidence}”</span></div>}
      </>
    );
  };
  const avatarCard = (name) => {
    const s = statsFor(name);
    return (
      <>
        <h4>{name}</h4>
        <div className="wb-hc-loc" style={{ fontSize: 13 }}>Home city · {s.home}</div>
        <div className="wb-sec"><div className="wb-k">This month</div>{s.explicit_days} days with an explicit signal · {s.home_days} defaulted to home city · {s.travel_days} travel day(s)</div>
        <div className="wb-sec"><div className="wb-k">Cities seen</div>{s.cities.join(', ')}</div>
      </>
    );
  };
  const infoCard = (
    <>
      <h4>How locations are inferred</h4>
      <div className="wb-sec"><div className="wb-k">Priority (per Shanti’s spec)</div>1 · Calendar <b>working location</b> (strongest)<br />2 · an <b>all-day event titled with a place</b><br />3 · <b>flights / hotels</b> mark travel days (✈)<br />4 · otherwise the person’s <b>home city</b> — never “Home” or “?”</div>
      <div className="wb-sec"><div className="wb-k">Reading the grid</div><b>Filled</b> pill = confirmed from the calendar. <b>Dashed / hollow</b> pill = home city, inferred (no signal that day) — hold it lightly.</div>
      <div className="wb-sec"><div className="wb-k">Source</div>Read nightly from each teammate’s Google Calendar via secure service-account access — no sharing required.</div>
    </>
  );
  const setLocCard = (
    <>
      <h4>Set your location</h4>
      <div className="wb-hc-loc" style={{ fontSize: 13 }}>Make a dashed cell solid</div>
      <div className="wb-sec"><div className="wb-k">Now</div>Opens Google Calendar → set a <b>working location</b> for any day; the grid picks it up on the next nightly run.</div>
      <div className="wb-sec"><div className="wb-k">On the platform (coming)</div>You’re signed in with Google, so you’ll set it <b>inline</b> — click a day in your own row, pick a city, and it writes a working-location event to your calendar. No leaving the page.</div>
    </>
  );

  return (
    <div className="wb-wrap">
      <div className="wb-card">
        <div className="wb-top">
          <div>
            <div className="wb-title">Team whereabouts
              <span className="wb-ii" onMouseEnter={(e) => peek(infoCard, e)} onMouseLeave={drop}>i</span>
            </div>
            <div className="wb-sub">
              Pulled from Google Calendar, refreshed overnight
              {freshness.label && (
                <> · <span style={{ color: freshness.isStale ? '#f59e0b' : '#6b7280' }}
                          title={freshness.computedAt ? freshness.computedAt.toLocaleString() : ''}>
                  data {freshness.label}
                </span></>
              )}
              {' '}<button type="button" className="wb-refresh" onClick={refresh} disabled={isValidating}
                      title="Re-read the latest board">{isValidating ? 'refreshing…' : '↻ refresh'}</button>
              {err && (
                <> · <span style={{ color: '#dc2626' }}
                          title={String(err)}>last refresh failed — showing previous data</span></>
              )}
              <span style={{ color: '#6b7280' }}> · hover any cell for the source</span>
            </div>
            <a className="wb-setloc" href={SET_LOC_URL} target="_blank" rel="noreferrer"
               onMouseEnter={(e) => peek(setLocCard, e)} onMouseLeave={drop}>✎ Set your location</a>
          </div>
          <div className="wb-nav">
            <button disabled={mi <= 0} onClick={() => goTo(Math.max(0, mi - 1))}>←</button>
            <div className="wb-mo">{M.label}</div>
            <button disabled={mi >= data.months.length - 1} onClick={() => goTo(Math.min(data.months.length - 1, mi + 1))}>→</button>
          </div>
        </div>
        <div style={{ borderBottom: '1px solid #23272f', margin: '16px 0 14px' }} />

        <table className="wb-table">
          <tbody>
            <tr>
              <td />
              {data.team.map((p) => (
                <td key={p.name} style={{ textAlign: 'center', padding: '0 4px 12px' }}>
                  <div className="wb-ava" onMouseEnter={(e) => peek(avatarCard(p.name), e)} onMouseLeave={drop}>{p.initials}</div>
                  <div style={{ fontSize: 12.5, color: '#e5e7eb' }}>{p.name}</div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>{p.home || statsFor(p.name).home}</div>
                </td>
              ))}
            </tr>
            {monthDays(M).map((iso) => {
              const today = iso === data.today;
              const weekend = isWeekend(iso);
              const rowCls = [today ? 'wb-tr-today' : '', weekend ? 'wb-tr-weekend' : ''].filter(Boolean).join(' ');
              return (
                <tr key={iso} className={rowCls}>
                  <td className="wb-dcol">{dlabel(iso)}</td>
                  {data.team.map((p) => {
                    const st = M.grid[p.name] && M.grid[p.name][iso];
                    if (!st) return <td key={p.name} style={{ padding: '3px 4px' }} />;
                    const [bg, fg] = color(st.loc);
                    let cls = 'wb-pill', style;
                    if (st.src === 'home_base') { cls = 'wb-pill inferred'; style = { color: fg }; }
                    else if (st.src === 'ooo') { style = { background: bg, color: fg }; }
                    else { cls = 'wb-pill dot'; style = { background: bg, color: fg }; }
                    return (
                      <td key={p.name} style={{ padding: '3px 4px' }}>
                        <div className={cls} style={style}
                             onMouseEnter={(e) => peek(cellCard(p.name, iso, st), e)} onMouseLeave={drop}>
                          {cellLabel(st)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="wb-legend">
          {seenCities.map((c) => {
            const [, fg] = color(c);
            return (
              <span key={c} className="wb-leg-item">
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: fg, display: 'inline-block' }} />{c}
              </span>
            );
          })}
          <span className="wb-leg-item">
            <span style={{ display: 'inline-block', width: 20, height: 13, border: '1.3px dashed #9ca3af', borderRadius: 5, opacity: 0.75 }} />home city · inferred (no calendar signal)
          </span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>✈ = travel day · 🌴 = out of office (city still shown) · hover for source</span>
        </div>
        <div className="wb-note">In Slack: type <code>/whereabouts</code> anywhere for a quick live check, or see the weekly board in <b>#navi</b>. · Home city fills when no calendar signal is found (never “Home”/“?” — per Shanti’s notes). Roster is read live from the team directory, so it follows joiners and leavers on its own; home cities per spec v1.0.0. Data via team-calendar service-account read. Dots (·) mark cells with an explicit calendar source. Weekends are shown but recessed.</div>
      </div>

      {hover && (
        <div className="wb-hc" style={{ left: hover.x, top: hover.y }} onMouseEnter={holdCard} onMouseLeave={drop}>
          {hover.content}
        </div>
      )}
    </div>
  );
}
